import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ContractContextNavigation from '../../components/ui/ContractContextNavigation';
import ContractHeader from './components/ContractHeader';
import TabNavigation from './components/TabNavigation';
import GeneralInfoTab from './components/GeneralInfoTab';
import SignaturesTab from './components/SignaturesTab';
import HistoryTab from './components/HistoryTab';
import JurisdictionTab from './components/JurisdictionTab';

const ContractDetails = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('general');
  const [contract, setContract] = useState({
    id: "CT-2024-001",
    title: "Master Service Agreement - TechCorp Solutions",
    status: "Active",
    value: 250000,
    startDate: "2024-01-15",
    endDate: "2025-01-14",
    client: "TechCorp Solutions",
    vendor: "Innovate Services LLC",
    clientContact: "John Mitchell (john.mitchell@techcorp.com)",
    vendorContact: "Sarah Johnson (sarah.johnson@innovateservices.com)",
    description: `Comprehensive master service agreement establishing the framework for ongoing technology consulting services between TechCorp Solutions and Innovate Services LLC.\n\nThis agreement covers software development, system integration, technical consulting, and ongoing support services for a 12-month period with options for renewal.`,
    paymentTerms: `Payment terms are Net 45 days from invoice date.\n\nInvoicing schedule:\n• Monthly invoicing for recurring services\n• Milestone-based invoicing for project work\n• Expenses reimbursed with proper documentation\n• Late payment penalty: 1.5% per month`,
    deliverables: `Key deliverables include:\n\n• Quarterly business reviews and reporting\n• Monthly technical status reports\n• Project-specific deliverables as defined in individual work orders\n• Documentation and knowledge transfer materials\n• 24/7 support for critical systems`,
    renewalTerms: `Contract automatically renews for additional 12-month periods unless either party provides 90 days written notice of non-renewal.\n\nRenewal terms subject to annual rate adjustments not to exceed 5% increase.`,
    governingLaw: "New York State Law",
    jurisdiction: "New York, NY",
    disputeResolution: "Binding Arbitration",
    venue: "New York County Supreme Court",
    arbitrationRules: "American Arbitration Association Commercial Rules"
  });

  // Get current tab from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams?.get('tab');
    
    if (location.pathname === '/milestone-management') {
      setActiveTab('milestones');
    } else if (location.pathname === '/document-management') {
      setActiveTab('attachments');
    } else if (tabParam) {
      setActiveTab(tabParam);
    } else {
      setActiveTab('general');
    }
  }, [location]);

  const handleContractUpdate = (updatedData) => {
    setContract(prev => ({
      ...prev,
      ...updatedData
    }));
    console.log('Contract updated:', updatedData);
  };

  const handleEdit = () => {
    console.log('Edit contract:', contract?.id);
    // Navigate to contract editor or open edit modal
  };

  const handleExport = () => {
    console.log('Export contract:', contract?.id);
    // Implement export functionality
  };

  const handleArchive = () => {
    console.log('Archive contract:', contract?.id);
    // Implement archive functionality
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <GeneralInfoTab 
            contract={contract} 
            onUpdate={handleContractUpdate}
          />
        );
      case 'signatures':
        return (
          <SignaturesTab 
            contractId={contract?.id}
            onUpdate={handleContractUpdate}
          />
        );
      case 'history':
        return (
          <HistoryTab 
            contractId={contract?.id}
          />
        );
      case 'jurisdiction':
        return (
          <JurisdictionTab 
            contract={contract}
            onUpdate={handleContractUpdate}
          />
        );
      case 'milestones':
        return (
          <div className="p-6 bg-background">
            <div className="max-w-6xl mx-auto text-center py-12">
              <div className="text-muted-foreground mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Milestone Management</h3>
              <p className="text-muted-foreground">
                Navigate to the dedicated Milestone Management page for detailed milestone tracking and management.
              </p>
            </div>
          </div>
        );
      case 'attachments':
        return (
          <div className="p-6 bg-background">
            <div className="max-w-6xl mx-auto text-center py-12">
              <div className="text-muted-foreground mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Document Management</h3>
              <p className="text-muted-foreground">
                Navigate to the dedicated Document Management page for file uploads and document organization.
              </p>
            </div>
          </div>
        );
      default:
        return (
          <GeneralInfoTab 
            contract={contract} 
            onUpdate={handleContractUpdate}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContractContextNavigation 
        contractId={contract?.id}
        contractTitle={contract?.title}
      />
      <main className="pt-32">
        <ContractHeader 
          contract={contract}
          onEdit={handleEdit}
          onExport={handleExport}
          onArchive={handleArchive}
        />
        
        <TabNavigation contractId={contract?.id} />
        
        {renderTabContent()}
      </main>
    </div>
  );
};

export default ContractDetails;